import { useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { canManageReport } from "@/profitability/access";
import { InsightsTab } from "@/profitability/insights";
import { ReadingSkeleton } from "@/profitability/insights/insights-skeleton";
import type { ReportPane } from "@/profitability/interactive-report/report-panes";
import {
  DeclaredPaneSkeleton,
  ReportSkeleton,
} from "@/profitability/interactive-report/report-skeleton";
import { Overview } from "@/profitability/overview";
import { missingUpload } from "@/profitability/overview/missing-upload";
import { OverviewFigures } from "@/profitability/overview/overview-figures";
import { OverviewInsights } from "@/profitability/overview/overview-insights";
import { FiguresSkeleton } from "@/profitability/overview/overview-skeleton";
import { cachedPeriodOutput } from "@/profitability/overview/query";
import type { RecordObject } from "@/profitability/records/objects";
import { useRetryProcessing } from "@/profitability/review/query";
import type { ProfitabilityReport, ReportOutput } from "@/profitability/types";
import { reportError, runDates } from "@/profitability/utils";
import { ReviewStage } from "./review-stage";
import { DocumentsSkeleton, TemplateDocuments } from "./template-documents";
import { TemplatePanes } from "./template-panes";
import { paneOf } from "./template-tabs";
import { TemplateWorkbook } from "./template-workbook";
import { useEmptyActions } from "./use-empty-actions";
import type { usePageEdge } from "./use-page-edge";
import type { TemplatePeriod } from "./use-template-period";

/**
 * The selected authored view or one of the shared period surfaces.
 */
export const TemplateSections = ({
  businessId,
  report,
  tab,
  period,
  periodId,
  compare,
  output,
  objects,
  panes,
  review,
  edge,
  onPaneChange,
  onLatest,
  recordsPending,
}: {
  businessId: string;
  report: ProfitabilityReport;
  tab: string;
  period: TemplatePeriod;
  periodId: string | null;
  compare: string | null;
  output: ReportOutput | undefined;
  objects: RecordObject[];
  panes: ReportPane[];
  /** Whether the page has a Review tab, which signs the period off. */
  review: boolean;
  edge: ReturnType<typeof usePageEdge>;
  onPaneChange: (pane: string) => void;
  /** Shows the latest period instead of the one on screen. */
  onLatest: () => void;
  /** The period's rows are still loading. */
  recordsPending: boolean;
}) => {
  const { run } = period;
  const client = useQueryClient();
  const knownOutput =
    output ?? cachedPeriodOutput(client, businessId, report.id);
  const actions = useEmptyActions(report, period.overview.data?.running);
  const retry = useRetryProcessing(businessId, run?.id ?? "");
  const shownPane = paneOf(tab);
  const selected = edge.record?.ref ?? null;
  const periodError = (title: string) => (
    <ErrorState
      title={title}
      description={reportError(period.overview.error)}
      retrying={period.overview.isFetching}
    >
      <Button onClick={() => void period.overview.refetch()}>Try again</Button>
    </ErrorState>
  );
  if (tab === "dashboard")
    return (
      <section aria-label="Dashboard" className="flex flex-1 flex-col">
        {period.findingsReview.error && (
          <Stack direction="row" gap={3}>
            <Text role="alert" tone="destructive">
              {period.findingsReview.error}
            </Text>
            {canManageReport(report) && (
              <Button
                variant="secondary"
                disabled={retry.isPending}
                onClick={() => retry.mutate("review")}
              >
                Retry checks
              </Button>
            )}
          </Stack>
        )}
        <Overview
          businessId={businessId}
          report={report}
          objects={objects}
          onOpenRecord={edge.openRecord}
          onAsk={edge.ask}
          periodId={periodId}
          compare={compare}
          period={period}
          recordsPending={recordsPending}
          chatOpen={!!edge.chat}
        />
      </section>
    );
  if (tab === "review")
    return (
      <ReviewStage
        businessId={businessId}
        report={report}
        objects={objects}
        period={period}
        periodId={periodId}
        edge={edge}
      />
    );
  if (tab === "insights") {
    const overview = period.overview.data;
    const previous = overview?.previous;
    const dates = run ? runDates(run) : null;
    const lines = (overview?.insights ?? []).filter((insight) =>
      insight.text.trim(),
    );
    let lead: ReactNode = null;
    if (!period.overview.isError && overview && overview.figures.length > 0) {
      lead = period.periodChanging ? (
        <FiguresSkeleton count={overview.figures.length} />
      ) : (
        <OverviewFigures
          figures={overview.figures}
          compared={!!previous?.comparable}
        />
      );
    }
    let reading: ReactNode = null;
    if (overview && lines.length > 0) {
      reading = period.periodChanging ? (
        <ReadingSkeleton count={lines.length} />
      ) : (
        <OverviewInsights
          businessId={businessId}
          insights={overview.insights}
          onAsk={(insight) => edge.ask(`Why: ${insight.text}`)}
          action={(insight) =>
            run
              ? missingUpload({
                  insight,
                  report,
                  objects,
                  run: { id: run.id, dates },
                })
              : null
          }
        />
      );
    }
    return (
      <section aria-label="Insights" className="flex flex-1 flex-col">
        <InsightsTab
          businessId={businessId}
          reportId={report.id}
          objects={objects}
          throughRunId={
            period.periodChanging ? period.requestedId : (run?.id ?? null)
          }
          published={report.published}
          later={
            !!run &&
            !!overview?.latest_run_id &&
            overview.latest_run_id !== run.id
          }
          building={!run && !!overview?.running}
          actions={{ ...actions, latest: onLatest }}
          lead={lead}
          reading={reading}
        />
      </section>
    );
  }
  if (tab === "workbook")
    return (
      <section aria-label="Workbook" className="flex min-h-0 flex-1 flex-col">
        <TemplateWorkbook
          businessId={businessId}
          report={report}
          period={period}
          output={output}
          objects={objects}
          edge={edge}
        />
      </section>
    );
  const failed = period.overview.isError;
  const holding =
    !failed && (period.overview.isPending || period.periodChanging);
  if (shownPane)
    return (
      <section
        aria-label={
          panes.find((item) => item.id === shownPane)?.title ?? "Report"
        }
        className="flex min-h-0 flex-1 flex-col"
      >
        {failed ? (
          periodError("The report could not load")
        ) : output && run && !period.periodChanging ? (
          <TemplatePanes
            key={run.id}
            businessId={businessId}
            reportId={report.id}
            run={run}
            output={output}
            pane={shownPane}
            objects={objects}
            selected={selected}
            review={review}
            findingsReview={period.findingsReview}
            onPaneChange={onPaneChange}
            onOpenRecord={edge.openRecord}
            onOpenRow={edge.clearEdge}
          />
        ) : (holding || recordsPending) && knownOutput ? (
          <DeclaredPaneSkeleton output={knownOutput} pane={shownPane} />
        ) : holding || recordsPending ? (
          <ReportSkeleton tabs={false} />
        ) : null}
      </section>
    );
  if (tab === "documents")
    return (
      <section
        aria-label="Documents"
        className="flex w-full min-w-0 flex-col max-lg:overflow-y-auto lg:min-h-0 lg:flex-1"
      >
        {failed ? (
          periodError("Documents could not load")
        ) : run && !period.periodChanging ? (
          <TemplateDocuments
            businessId={businessId}
            report={report}
            run={run}
            periodId={periodId}
            compare={compare}
            output={output}
            objects={objects}
            onAsk={edge.ask}
          />
        ) : (
          holding && <DocumentsSkeleton rows={(report.inputs ?? []).length} />
        )}
      </section>
    );
  return null;
};
